import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ChevronRight } from 'lucide-react';
import axios from 'axios';
import ShopLayout from '../components/layout/ShopLayout';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { name: 'Skincare', slug: 'skincare', image: 'https://images.unsplash.com/photo-1571206508927-2ef3026ada5d?w=400', description: 'Cleansers, serums & more' },
  { name: 'Makeup', slug: 'makeup', image: 'https://images.unsplash.com/photo-1523374488371-bf3bafcf86bb?w=400', description: 'Lips, eyes, face' },
  { name: 'Haircare', slug: 'haircare', image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400', description: 'Treatments & styling' },
  { name: 'Fragrance', slug: 'fragrance', image: 'https://images.unsplash.com/photo-1729101807924-3446ca9aa480?w=400', description: 'Perfumes & body mists' },
];

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/api/products/featured`);
        setFeaturedProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  return (
    <ShopLayout>
      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[85vh] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1626857899821-a0e62fb04721?w=1600"
            alt="Beauty hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent"></div>
        </div>
        
        <div className="relative max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 h-full flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">
              Curated Beauty Essentials
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-medium tracking-tight leading-[1.1] mb-6">
              Your Glow,<br />
              <span className="italic">Elevated</span>
            </h1>
            <p className="text-neutral-600 text-lg mb-8 max-w-md">
              Discover top-rated beauty products handpicked for the modern woman. 
              Shop trusted brands at the best prices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/category/skincare">
                <Button 
                  className="h-14 px-10 bg-[#1A1A1A] text-white hover:bg-neutral-800 rounded-none uppercase tracking-widest text-xs font-bold btn-shine"
                  data-testid="hero-shop-now-btn"
                >
                  Shop Now
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/category/makeup">
                <Button 
                  variant="outline"
                  className="h-14 px-10 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none uppercase tracking-widest text-xs font-bold"
                  data-testid="hero-explore-btn"
                >
                  Explore Makeup
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 md:py-28" data-testid="categories-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Browse by</p>
              <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight">
                Shop Categories
              </h2>
            </div>
            <Link 
              to="/category/skincare" 
              className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-neutral-500 transition-colors"
            >
              View All
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {CATEGORIES.map((category, index) => (
              <motion.div
                key={category.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={`/category/${category.slug}`}
                  className="group block relative aspect-[3/4] overflow-hidden"
                  data-testid={`category-card-${category.slug}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 category-overlay"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="font-heading text-xl md:text-2xl font-medium mb-1">
                      {category.name}
                    </h3>
                    <p className="text-white/70 text-sm">{category.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 md:py-28 bg-secondary" data-testid="featured-products-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Hand-picked for you</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal tracking-tight">
              Bestsellers & New Arrivals
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <ProductCard product={product} />
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/category/skincare">
              <Button
                variant="outline"
                className="h-14 px-10 border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-none uppercase tracking-widest text-xs font-bold"
                data-testid="view-all-products-btn"
              >
                View All Products
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Trust Banner */}
      <section className="py-16 border-y" data-testid="trust-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-12 h-12 bg-secondary mx-auto mb-4 flex items-center justify-center">
                <Star className="w-6 h-6" />
              </div>
              <h3 className="font-heading text-lg font-medium mb-2">Top-Rated Products</h3>
              <p className="text-sm text-neutral-500">Only the best-reviewed beauty products</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-secondary mx-auto mb-4 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-heading text-lg font-medium mb-2">Best Prices</h3>
              <p className="text-sm text-neutral-500">Competitive Amazon pricing</p>
            </div>
            <div>
              <div className="w-12 h-12 bg-secondary mx-auto mb-4 flex items-center justify-center">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-heading text-lg font-medium mb-2">Trusted Brands</h3>
              <p className="text-sm text-neutral-500">Verified authentic products</p>
            </div>
          </div>
        </div>
      </section>
    </ShopLayout>
  );
};

export default HomePage;
